import { useCallback, useRef } from 'react'
import { ERAS } from '../config/eras'
import { resolveEra } from '../core/time/timeEngine'
import { useExperienceStore } from '../store/experienceStore'
import { Z_STACK } from '../config/layout'

export function TimelineBar() {
  const timeValue = useExperienceStore((s) => s.timeValue)
  const timeSpeed = useExperienceStore((s) => s.timeSpeed)
  const setTimeAbsolute = useExperienceStore((s) => s.setTimeAbsolute)
  const setUiDragging = useExperienceStore((s) => s.setUiDragging)
  const setInputMode = useExperienceStore((s) => s.setInputMode)
  const trackRef = useRef<HTMLDivElement>(null)
  const era = resolveEra(timeValue, ERAS)

  const posFromEvent = useCallback((clientX: number) => {
    const el = trackRef.current
    if (!el) return 0
    const r = el.getBoundingClientRect()
    return Math.min(1, Math.max(0, (clientX - r.left) / r.width))
  }, [])

  const onPointerDown = (e: React.PointerEvent) => {
    e.currentTarget.setPointerCapture(e.pointerId)
    setUiDragging(true)
    setInputMode('mouse')
    setTimeAbsolute(posFromEvent(e.clientX))
  }

  const onPointerMove = (e: React.PointerEvent) => {
    if (!e.currentTarget.hasPointerCapture(e.pointerId)) return
    setTimeAbsolute(posFromEvent(e.clientX))
  }

  const onPointerUp = (e: React.PointerEvent) => {
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId)
    }
    setUiDragging(false)
  }

  const speedLabel =
    Math.abs(timeSpeed) < 0.01
      ? '暂停'
      : timeSpeed < 0
        ? `倒流 ×${Math.abs(timeSpeed / 0.28).toFixed(1)}`
        : `前进 ×${(timeSpeed / 0.28).toFixed(1)}`

  return (
    <div
      className="timeline-bar"
      style={{
        position: 'fixed',
        left: '50%',
        bottom: 28,
        transform: 'translateX(-50%)',
        width: 'min(720px, 88vw)',
        zIndex: Z_STACK.ui,
        pointerEvents: 'auto',
      }}
    >
      <div className="timeline-meta">
        <span className="era-name">{era.labelZh}</span>
        <span className="speed-label">{speedLabel}</span>
      </div>
      <div
        ref={trackRef}
        className="timeline-track"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        role="slider"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(timeValue * 100)}
        aria-label="时间轴"
        tabIndex={0}
      >
        <div className="timeline-eras">
          {ERAS.map((e) => (
            <div
              key={e.id}
              className="era-tick"
              style={{
                left: `${((e.range[0] + e.range[1]) / 2) * 100}%`,
              }}
              title={e.labelZh}
            />
          ))}
        </div>
        <div className="timeline-fill" style={{ width: `${timeValue * 100}%` }} />
        <div className="timeline-thumb" style={{ left: `${timeValue * 100}%` }} />
      </div>
      <div className="timeline-labels">
        <span>大爆炸</span>
        <span>现代</span>
      </div>
    </div>
  )
}
